/**
 * Local footprint registry.
 *
 * Real designs use footprints that were never in the stock libraries — a board
 * carries custom parts (a big mounting pad, a bespoke transformer) that the
 * author drew once and reused. CIRCAD kept those in a personal `.LIB`; here we
 * harvest them from any board that is imported, so a later netlist-in can
 * resolve a footprint the shipped catalogue does not have.
 *
 * This is a fallback only: the stock libraries are consulted first, and the
 * registry fills the gaps. It lives in the browser, so it works the same in
 * development and in production without touching the server.
 */

import type { CadDocument, ComponentEntity, Entity } from '@sircad/core/model.ts';
import { instantiate } from '@sircad/core/geom/transform.ts';
import type { SavedPart } from './parts.ts';

/**
 * Every placeable part in a design, as library parts.
 *
 * A board yields footprints, a schematic yields symbols; both are kept as
 * finished-geometry components. Drawing/port blocks (`$$…`) and padless
 * components are skipped, and each name is taken once. The result is ready to
 * persist into the user's personal library.
 */
export function harvestParts(doc: CadDocument): SavedPart[] {
  const out: SavedPart[] = [];
  const seen = new Set<string>();
  for (const e of doc.entities) {
    if (e.kind !== 'component' || e.name.startsWith('$$')) continue;
    if (!e.children.some((c) => c.kind === 'pad')) continue;
    // A board part is filed under its pattern where it has one. Named after
    // the designator instead, every capacitor on the board becomes a separate
    // library part called C1, C2, C3 — and none of them is findable by the
    // name a schematic uses to ask for it.
    const name = (doc.kind === 'pcb' ? (e.footprint ?? e.name) : e.name).trim();
    const key = name.toUpperCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    out.push({
      kind: doc.kind,
      name,
      description: (e.params[2] ?? '').trim(),
      category: 'Imported',
      // A board component *is* its footprint; a symbol names the mask it maps to.
      footprint: doc.kind === 'pcb' ? name : (e.footprint ?? null),
      sections: 1,
      data: { component: cleanComponent(e, doc.kind) },
    });
  }
  return out;
}

/**
 * A reusable template of a placed component: fresh ids, at the origin, with the
 * board's own net names dropped from its pads. For a symbol the reference
 * designator is reset to its prefix (`C28` → `C?`) so annotation renumbers it.
 */
export function cleanComponent(e: ComponentEntity, kind: 'sch' | 'pcb'): ComponentEntity {
  const copy = instantiate(e);
  copy.x = 0;
  copy.y = 0;
  const strip = (list: Entity[]): void => {
    for (const c of list) {
      if ('signal' in c) delete (c as { signal?: string }).signal;
      if (c.kind === 'component') strip(c.children);
    }
  };
  strip(copy.children);
  if (kind === 'sch' && copy.params[1]) {
    copy.params = { ...copy.params, 1: copy.params[1].replace(/\d+\s*$/, '') + '?' };
  }
  return copy;
}

export class FootprintRegistry {
  /** Keyed by upper-cased footprint name. */
  private map = new Map<string, ComponentEntity>();
  private readonly collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  get size(): number {
    return this.map.size;
  }

  names(): string[] {
    return [...this.map.values()].map((c) => c.name);
  }

  /**
   * Harvest the named footprints from a board. Returns how many *new* ones were
   * added. Drawing/port blocks (`$$…`) and components with no pads are skipped;
   * the first instance of each name wins.
   */
  harvest(doc: CadDocument): number {
    if (doc.kind !== 'pcb') return 0;
    let added = 0;
    for (const e of doc.entities) {
      if (e.kind !== 'component' || e.name.startsWith('$$')) continue;
      if (!e.children.some((c) => c.kind === 'pad')) continue;

      // Filed under the *pattern*, falling back to the name. A board from
      // CIRCAD names its components after the footprint, so the two are the
      // same; one from Altium or KiCad names them after the reference
      // designator, and filing `CAPC1005X55N` under `C15` means a schematic
      // asking for the pattern it actually named finds nothing. That is why
      // loading a netlist from an imported sheet placed nothing at all.
      const pattern = (e.footprint ?? e.name).trim();
      const key = pattern.toUpperCase();
      if (!key || this.map.has(key)) continue;
      // Named after the pattern too, not just filed under it. A board part is
      // its footprint, and a template still called `C15` would land on the
      // board under that name however many other parts it was placed for.
      const template = this.template(e);
      template.name = pattern;
      this.map.set(key, template);
      added++;
    }
    return added;
  }

  /**
   * Resolve a footprint name or mask to a placeable component, or null. Exact
   * name first; a `*`/`?` mask matches by glob, the natural-sort winner taken so
   * `R*` prefers R300 over R3000 — the same rule the libraries use.
   */
  find(name: string): ComponentEntity | null {
    const want = name.trim();
    if (!want) return null;

    const exact = this.map.get(want.toUpperCase());
    if (exact) return instantiate(exact);

    if (/[*?]/.test(want)) {
      const rx = new RegExp(
        '^' + want.replace(/[.+^${}()|[\]\\]/g, (c) => '\\' + c)
          .replace(/\*/g, '.*').replace(/\?/g, '.') + '$',
        'i',
      );
      const hits = [...this.map.keys()].filter((k) => rx.test(k))
        .sort((a, b) => this.collator.compare(a, b) || a.localeCompare(b));
      const first = hits[0];
      if (first) return instantiate(this.map.get(first)!);
    }
    return null;
  }

  /**
   * A clean template: fresh ids, at the origin, board-specific net names
   * stripped from its pads — the transfer assigns those from the schematic. The
   * pins are kept, since they are what the signals attach to.
   */
  private template(e: ComponentEntity): ComponentEntity {
    return cleanComponent(e, 'pcb');
  }
}
